import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, ArrowLeft, Users, Sparkles, BookOpen, Headphones } from "lucide-react";
import api from "../api/axios";
import SEO from "../components/SEO";
import { stripHtml, truncate } from "../utils/text";

export default function CategoryDetail() {
  const { id } = useParams();
  const [category, setCategory] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [catRes, artRes] = await Promise.all([
        api.get(`/categories`), // We'll filter from the list or add a specific route later
        api.get(`/articles?categoryId=${id}`)
      ]);
      
      const currentCat = catRes.data.find(c => c.id === id);
      setCategory(currentCat);
      setArticles(artRes.data);
    } catch (err) {
      console.error("Failed to load category wisdom:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-spiritual-white dark:bg-gray-950">
        <div className="w-8 h-8 rounded-full border-4 border-gray-100 border-t-brand-blue animate-spin"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-spiritual-white dark:bg-gray-950">
        <h2 className="text-2xl font-display font-medium mb-4 text-gray-500">This path is currently hidden.</h2>
        <Link to="/explore" className="text-brand-blue hover:underline">Return to Explore</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-spiritual-white dark:bg-gray-950 transition-colors duration-500">
      <SEO 
        title={`${category.label} Wisdom`}
        description={`Explore articles, guidance, and community wisdom regarding ${category.label} at Antaryatra.`}
        url={`/wisdom/${id}`}
      />

      <main className="max-w-5xl mx-auto px-6 py-16 md:py-24">
        {/* Navigation */}
        <Link 
          to="/explore" 
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-brand-blue mb-12 group transition-all"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1" /> Back to Explore
        </Link>

        {/* Header Section */}
        <header className="mb-16 space-y-6">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-brand-blue rounded-full text-sm font-semibold tracking-wide border border-blue-100 dark:border-blue-800/50">
            <Compass className="w-4 h-4" /> The Pathway of Wisdom
          </div>
          <h1 className="text-4xl md:text-6xl font-display font-bold text-gray-900 dark:text-gray-100 leading-tight">
            {category.label}
          </h1>
          <p className="text-lg md:text-xl text-gray-500 dark:text-gray-400 font-light max-w-2xl leading-relaxed">
            A curated collection of reflections and guidance dedicated to the journey of {category.label.toLowerCase()}.
          </p>
        </header>

        {/* Articles Grid */}
        <section className="grid md:grid-cols-2 gap-8 mb-24">
          {articles.length > 0 ? (
            articles.map((article, idx) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="group bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 overflow-hidden hover:shadow-xl transition-all duration-500"
              >
                <Link to={`/article/${article.slug || article.id}`}>
                  {article.coverImage && (
                    <div className="h-56 overflow-hidden">
                      <img 
                        src={article.coverImage} 
                        alt={article.title} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" 
                      />
                    </div>
                  )}
                  <div className="p-8 space-y-4">
                    <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-gray-100 group-hover:text-brand-blue transition-colors flex items-center gap-2">
                      {article.title}
                      {article.audioUrl && <Headphones size={16} className="text-brand-blue" />}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 line-clamp-3 text-sm leading-relaxed">
                      {truncate(stripHtml(article.excerpt || article.content), 160)}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-50 dark:border-gray-800">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5" /> Read Transmission
                      </span>
                      <span className="text-xs text-brand-blue font-medium">Read Article →</span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))
          ) : (
            <div className="md:col-span-2 text-center py-20 bg-gray-50 dark:bg-gray-900/50 rounded-3xl border border-dashed border-gray-200 dark:border-gray-800">
              <Sparkles className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-400 font-display">Wisdom is gathering on this path. Please check back soon.</p>
            </div>
          )}
        </section>

        {/* 🏹 THE DUAL CALL (Seekers & Guides) */}
        <section className="grid md:grid-cols-2 gap-8">
          {/* Seeker CTA */}
          <div className="bg-gradient-to-br from-brand-blue to-blue-700 rounded-3xl p-10 text-white shadow-xl relative overflow-hidden group">
            <div className="relative z-10 space-y-6">
              <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-3xl font-display font-bold">Seek Support</h3>
              <p className="text-blue-100 leading-relaxed">
                If the wisdom above resonates with your current struggle, you don't have to carry it alone. Enter the Sanctuary and find a listener.
              </p>
              <Link 
                to="/signup" 
                className="inline-block px-8 py-3 bg-white text-brand-blue font-bold rounded-xl hover:bg-blue-50 transition-colors shadow-lg"
              >
                Join as a Seeker
              </Link>
            </div>
          </div>

          {/* Guide CTA */}
          <div className="bg-white dark:bg-gray-900 border-2 border-brand-blue rounded-3xl p-10 shadow-xl relative overflow-hidden group">
            <div className="relative z-10 space-y-6">
              <div className="w-12 h-12 bg-brand-blue/10 rounded-2xl flex items-center justify-center">
                <Users className="w-6 h-6 text-brand-blue" />
              </div>
              <h3 className="text-3xl font-display font-bold text-gray-900 dark:text-gray-100">Become a Guide</h3>
              <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                Do you have wisdom to share? Antaryatra is a platform for those who listen with empathy and guide with integrity.
              </p>
              <Link 
                to="/apply" 
                className="inline-block px-8 py-3 bg-brand-blue text-white font-bold rounded-xl hover:bg-blue-800 transition-colors shadow-lg"
              >
                Apply as a Guide
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
